import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import Alert from '~/Common-Objects/UI/Alert'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import Dialog from '~/Common-Objects/UI/Dialog'
import Select from '~/Common-Objects/UI/Forms/Select/Select'
import SquareChip from '~/Common-Objects/UI/Chips/SquareChip.jsx'
import i18n from '~/i18n'
import InboxButton from '../../shared/inboxButton'
import axios from '~/data/http/axios'

export default function ChatAnalysis({ chat, inbox, userRole, setActiveConversation = () => {}, chatIntents = [] }) {
    const betas = useSelector((state) => state.accountMaster.accountMaster.beta)
    const [override, setOverride] = useState(typeof chat.sentiment === 'number' || !!chat.ChatIntentId)
    const [editing, setEditing] = useState(false)
    const [analysing, setAnalysing] = useState(false)

    const runNow = async () => {
        setAnalysing(true)
        try {
            const res = await axios.get('/inboxes/chat/' + chat.id + '/analysis')
            setActiveConversation({ ...chat, ...res.data })
        } catch (e) {
            setAnalysing(false)
        }
    }

    let willUseAi = false
    if (betas.sentimentAnalysis && inbox.settings?.sentimentAnalysis?.some((c) => c === chat.InboxChannelId)) {
        willUseAi = true
    }
    if (typeof chat.sentiment === 'number' || chat.ChatIntentId) {
        willUseAi = false
    }
    if (override) willUseAi = false
    let sentimentLabel = '',
        sentimentClasses = ''

    const sentiment = !isNaN(chat.sentiment) ? parseInt(chat.sentiment) : ''
    if (sentiment === 1) {
        sentimentLabel = i18n.t('chat:rightBar.analysis.positive')
        sentimentClasses = 'square-chip-green'
    } else if (sentiment === 0) sentimentLabel = i18n.t('chat:rightBar.analysis.neutral')
    else if (sentiment === -1) {
        sentimentLabel = i18n.t('chat:rightBar.analysis.negative')
        sentimentClasses = 'square-chip-red'
    }

    let intentName = ''
    if (chat.ChatIntentId) {
        const intent = chatIntents.find((i) => i.id === chat.ChatIntentId)
        if (intent && intent.ChatIntentType) intentName = intent.ChatIntentType.name + ' - ' + intent.name
        else if (intent) intentName = intent.name
    }

    return (
        <div className='inbox-right-sidebar__sections_content'>
            {editing && (
                <SetManual
                    chat={chat}
                    chatIntents={chatIntents}
                    setActiveConversation={setActiveConversation}
                    onFinish={() => setEditing(false)}
                />
            )}
            {willUseAi && (
                <Alert
                    type='info'
                    cta={
                        <div className='quickFlex' style={{ gap: 5 }}>
                            <Button className='button-sml' onClick={runNow} disabled={analysing}>
                                Run Now
                            </Button>
                            <Button
                                disabled={analysing}
                                className='button-sml'
                                onClick={() => {
                                    setOverride(true)
                                    setEditing(true)
                                }}
                            >
                                Override
                            </Button>
                        </div>
                    }
                >
                    {i18n.t('chat:inbox.rightBar.analysis.aiEnabled')}
                </Alert>
            )}
            {!willUseAi && (
                <div className='quickFlex' style={{ gap: 5, flexWrap: 'wrap' }}>
                    {sentimentLabel !== '' && (
                        <SquareChip className={sentimentClasses} onClick={() => setEditing(true)}>
                            {sentimentLabel}
                        </SquareChip>
                    )}
                    {intentName !== '' && (
                        <SquareChip onClick={() => setEditing(true)}>{intentName}</SquareChip>
                    )}
                    {sentimentLabel === '' && intentName === '' && (
                        <Button
                            disabled={analysing}
                            className='button-sml'
                            onClick={() => {
                                setOverride(true)
                                setEditing(true)
                            }}
                        >
                            Select Manually
                        </Button>
                    )}
                </div>
            )}
        </div>
    )
}

function SetManual({ chat, chatIntents, setActiveConversation = () => {}, onFinish = () => {} }) {
    const [sentiment, setSentiment] = useState(chat.sentiment === '' ? undefined : chat.sentiment)
    const [ChatIntentId, setChatIntentId] = useState(chat.ChatIntentId)
    const [open, setOpen] = useState(false)
    const [saving, setSaving] = useState(false)

    const submit = async () => {
        // submit to api
        setSaving(true)
        const body = {
            sentiment,
            ChatIntentId,
            ChatIntentTypeId: chatIntents.find((i) => i.id === ChatIntentId)?.ChatIntentTypeId || null
        }
        await axios.put('/inboxes/chat/' + chat.id, body)
        setActiveConversation({ ...chat, ...body })
        setSaving(false)
        onFinish()
    }

    const style = {}
    if (open && open < 5) style.minHeight = 50 * open + 50
    else if (open && open >= 5) style.height = 350

    return (
        <Dialog.Root open={true} onOpenChange={onFinish}>
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content className='inbox__generic-modal'>
                    <div style={style}>
                        <div className='inbox__generic-modal__header'>Set the analysis values</div>
                        <div className='inbox__generic-modal__description'>
                            <Select
                                outerClassName='mar-b10'
                                onOpen={() => setOpen(4)}
                                onClose={() => setOpen(false)}
                                label='Sentiment'
                                placeholder='Select Sentiment'
                                value={sentiment}
                                onChange={(e) => setSentiment(e.target.value)}
                                data={[
                                    { label: 'Positive', value: 1 },
                                    { label: 'Neutral', value: 0 },
                                    { label: 'Negative', value: -1 }
                                ]}
                            />
                            <Select
                                label='Intent'
                                onOpen={() => setOpen(chatIntents.length)}
                                onClose={() => setOpen(false)}
                                value={ChatIntentId}
                                placeholder='Select Intent'
                                onChange={(e) => setChatIntentId(e.target.value)}
                                filter={true}
                                data={chatIntents.map((i) => ({
                                    label: i.name,
                                    value: i.id,
                                    group: i.ChatIntentType?.name
                                }))}
                            />
                        </div>
                        <div className='inbox__generic-modal__cta'>
                            <InboxButton onClick={submit} variant='secondary' disabled={saving}>
                                Save
                            </InboxButton>
                            <InboxButton onClick={onFinish} variant='tertiary'>
                                Cancel
                            </InboxButton>
                        </div>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    )
}
